/**
 * SearchAdviserDashboard — Personal Search Adviser
 *
 * Follows existing Dashboard.js conventions:
 *  - lucide-react icons
 *  - axios for API calls
 *  - sonner toasts
 *  - shadcn/ui components (card, button, input, badge, etc.)
 *  - Tailwind CSS classes only (no inline styles)
 *
 * Key UX rules:
 *  - Mock / simulated data is ALWAYS labeled with an orange badge.
 *  - Provisional scores show a clear disclaimer.
 *  - The AI Adviser chat grays out if no scan is selected.
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import "../components/adviser/adviser.css";
import PageSEO from '../components/PageSEO';
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { ThemeToggle } from "../components/ThemeToggle";
import {
  Search,
  Globe,
  Loader2,
  MessageSquare,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Activity,
  RefreshCw,
  ChevronRight,
  Zap,
  Bot,
  ArrowLeft,
  Send,
  Sparkles,
  LogOut,
} from "lucide-react";

import { PageAuditTable } from "../components/adviser/PageAuditTable";
import { ContentOpportunitiesBoard } from "../components/adviser/ContentOpportunitiesBoard";
import { ActionPlanTimeline } from "../components/adviser/ActionPlanTimeline";
import { IssuesAndFixesList } from "../components/adviser/IssuesAndFixesList";

// ─── Helper Components ──────────────────────────────────────────────────────

function StatusPill({ status }) {
  const map = {
    queued: { color: "bg-slate-500/10 text-slate-500 dark:bg-slate-400/10 dark:text-slate-400", label: "Queued", icon: Clock },
    running: { color: "bg-[#FEAB3D]/15 text-[#FEAB3D]", label: "Analyzing", icon: Loader2 },
    completed: { color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", label: "Complete", icon: CheckCircle2 },
    partial: { color: "bg-amber-500/10 text-amber-600 dark:text-amber-400", label: "Partial", icon: AlertTriangle },
    failed: { color: "bg-red-500/10 text-red-600 dark:text-red-400", label: "Failed", icon: AlertTriangle },
  };
  const cfg = map[status] || map.running;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide ${cfg.color}`}>
      <Icon className={`h-3 w-3 ${status === "running" ? "animate-spin" : ""}`} />
      {cfg.label}
    </span>
  );
}

/** AI Core — CSS visualization for empty state */
function AICore({ scale }) {
  return (
    <div className="adv-ai-core" aria-hidden="true" style={scale ? { transform: `scale(${scale})` } : undefined}>
      <div className="adv-ai-core__ring" style={{ width: 150, height: 150, top: '50%', left: '50%', marginTop: -75, marginLeft: -75 }}>
        <div className="adv-ai-core__node" style={{ top: 0, left: '50%' }} />
        <div className="adv-ai-core__node" style={{ bottom: 0, left: '50%', top: 'auto' }} />
      </div>
      <div className="adv-ai-core__ring" style={{ width: 100, height: 100, top: '50%', left: '50%', marginTop: -50, marginLeft: -50, animation: 'adv-orbit-reverse 25s linear infinite' }}>
        <div className="adv-ai-core__node" style={{ top: '50%', right: -3, left: 'auto' }} />
        <div className="adv-ai-core__node" style={{ top: '50%', left: -3 }} />
      </div>
      <div className="adv-ai-core__ring" style={{ width: 55, height: 55, top: '50%', left: '50%', marginTop: -27.5, marginLeft: -27.5, animation: 'adv-orbit 35s linear infinite' }}>
        <div className="adv-ai-core__node" style={{ top: 0, left: '50%' }} />
      </div>
      <div className="adv-ai-core__center flex items-center justify-center">
        <Sparkles className="h-4 w-4 text-[#021426]" />
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function SearchAdviserDashboard({ user, onLogout }) {
  // ── Scan State ──
  const [url, setUrl] = useState("");
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const navigate = useNavigate();
  const [scanStatus, setScanStatus] = useState(null);
  const [startingLoad, setStartingLoad] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pollerRef = useRef(null);
  const selectedScanIdRef = useRef(null);

  // ── Provider Status ──
  const [providerStatus, setProviderStatus] = useState(null);
  const providerRequestRef = useRef(0);

  // ── Adviser Chat ──
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  const chatGenerationRef = useRef(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [chatIssueId, setChatIssueId] = useState(null);
  const chatReady = !!scanResults && ["completed", "partial"].includes(scanStatus);
  const selectedDomain = selectedScan?.url?.replace(/^https?:\/\//, '').split('/')[0] || '';
  const chatIssue = scanResults?.issues?.find(issue => issue.id === chatIssueId);
  const suggestedPrompts = [
    ...(scanResults?.issues?.length ? ["What should I fix first on this site?", "Explain the highest-priority issue in this scan"] : []),
    ...(scanResults?.score ? ["Explain this site's readiness score"] : []),
    ...(scanResults?.content_opportunities?.length ? ["Which content opportunity from this scan should I work on first?"] : []),
  ];

  // ── Active Tab ──
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [tab, setTab] = useState("overview");

  // ── UI-only state (no business logic) ──
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const cursorGlowRef = useRef(null);
  const mainAreaRef = useRef(null);

  // ─── Mount ──────────────────────────────────────────────────────────────
  useEffect(() => {
    loadScans();
    loadProviderStatus();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  useEffect(() => {
    const generation = ++chatGenerationRef.current;
    setChatHistory([]);
    setChatInput("");
    setChatIssueId(null);
    setChatLoading(false);
    setHistoryLoading(chatReady);
    if (chatReady) {
      axios.get(`/adviser/scan/${selectedScan.id}/chat`).then(res => {
        if (chatGenerationRef.current === generation) setChatHistory(res.data.messages || []);
      }).catch(() => {
        if (chatGenerationRef.current === generation) toast.error("Could not load this scan's conversation.");
      }).finally(() => {
        if (chatGenerationRef.current === generation) setHistoryLoading(false);
      });
    }
    return () => { chatGenerationRef.current += 1; };
  }, [selectedScan?.id, user?.id, chatReady]);

  // ─── Cleanup poller on unmount ──────────────────────────────────────────
  useEffect(() => {
    return () => {
      selectedScanIdRef.current = null;
      if (pollerRef.current) clearInterval(pollerRef.current);
    };
  }, []);

  // ─── Header scroll detection ───────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setHeaderScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ─── Cursor glow (performance-safe: CSS variables, not React state) ──
  useEffect(() => {
    const el = mainAreaRef.current;
    if (!el) return;
    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isTouchDevice || prefersReducedMotion) return;

    let rafId = null;
    const onMove = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        if (cursorGlowRef.current) {
          cursorGlowRef.current.style.left = e.clientX + "px";
          cursorGlowRef.current.style.top = e.clientY + "px";
          cursorGlowRef.current.style.opacity = "1";
        }
        rafId = null;
      });
    };
    const onLeave = () => {
      if (cursorGlowRef.current) cursorGlowRef.current.style.opacity = "0";
    };
    el.addEventListener("mousemove", onMove, { passive: true });
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // ─── API Calls ─── (ALL PRESERVED EXACTLY)

  const loadScans = async () => {
    setIsRefreshing(true);
    try {
      const res = await axios.get("/adviser/scans");
      setScans(res.data.scans || []);
      const completed = (res.data.scans || []).find(s => s.status === "completed" || s.status === "partial");
      if (completed && !selectedScanIdRef.current) {
        selectScan(completed);
      }
    } catch {
      // silently ignore if adviser not yet set up
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadProviderStatus = async () => {
    const requestId = ++providerRequestRef.current;
    try {
      const res = await axios.get("/diagnostics/status");
      if (requestId === providerRequestRef.current) setProviderStatus(res.data);
    } catch {
      // ignore — diagnostics are optional
    }
  };

  const startScan = async () => {
    if (!url.trim()) {
      toast.error("Please enter a valid URL");
      return;
    }
    setStartingLoad(true);
    try {
      const res = await axios.post("/adviser/scan", { url: url.trim() });
      const { scan_id } = res.data;
      selectedScanIdRef.current = scan_id;
      chatGenerationRef.current += 1;
      setChatHistory([]);
      setChatInput("");
      setChatIssueId(null);
      setSelectedIssueId(null);
      setChatLoading(false);
      toast.success("Scan started! Polling for updates…");
      setUrl("");
      await loadScans();
      const fakeScan = { id: scan_id, status: "queued", url: url.trim() };
      setSelectedScan(fakeScan);
      setScanResults(null);
      setScanStatus("queued");
      startPolling(scan_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start scan");
    } finally {
      setStartingLoad(false);
    }
  };

  const selectScan = async (scan) => {
    if (pollerRef.current) clearInterval(pollerRef.current);
    selectedScanIdRef.current = scan.id;
    chatGenerationRef.current += 1;
    setChatInput("");
    setChatIssueId(null);
    setSelectedIssueId(null);
    setChatLoading(false);
    setSelectedScan(scan);
    setScanStatus(scan.status);
    setScanResults(null);
    setChatHistory([]);
    if (scan.status === "completed" || scan.status === "partial") {
      loadResults(scan.id);
    } else if (scan.status === "running" || scan.status === "queued") {
      startPolling(scan.id);
    }
  };

  const startPolling = (scan_id) => {
    if (pollerRef.current) clearInterval(pollerRef.current);
    let initialResultsLoaded = false;
    pollerRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`/adviser/scan/${scan_id}/status`);
        const currentScan = res.data;
        if (selectedScanIdRef.current !== scan_id) return;
        setScans(existing => existing.map(s => s.id === scan_id ? currentScan : s));
        setScanStatus(currentScan.status);
        setSelectedScan(currentScan);
        if (currentScan.initial_report_ready && !initialResultsLoaded) {
          initialResultsLoaded = true;
          loadResults(scan_id);
        }
        if (currentScan.status === "completed" || currentScan.status === "failed" || currentScan.status === "partial") {
          clearInterval(pollerRef.current);
          await loadScans();
          if (currentScan.status === "completed" || currentScan.status === "partial") {
            toast.success(currentScan.status === "partial" ? "Partial analysis is ready with available findings." : "Analysis is ready.");
            loadResults(scan_id);
          } else {
            toast.error("Scan failed: " + (currentScan.error_message || "Unknown error"));
          }
        }
      } catch {
        clearInterval(pollerRef.current);
      }
    }, 3000);
  };

  const loadResults = async (scan_id) => {
    setResultsLoading(true);
    try {
      const res = await axios.get(`/adviser/scan/${scan_id}/results`);
      if (res.status === 202 || selectedScanIdRef.current !== scan_id) return;
      setScanResults({ ...res.data.scan, ...res.data });
      loadProviderStatus();
    } catch (err) {
      if (err?.response?.status !== 202) {
        toast.error("Failed to load scan results");
      }
    } finally {
      setResultsLoading(false);
    }
  };

  const sendChat = async () => {
    if (!chatInput.trim() || !chatReady || chatLoading || historyLoading) return;
    const generation = chatGenerationRef.current;
    const msg = chatInput.trim();
    setChatInput("");
    setChatHistory(h => [...h, { role: "user", content: msg }]);
    setChatLoading(true);
    try {
      const res = await axios.post("/adviser/chat", {
        scan_id: selectedScan.id,
        message: msg,
        ...(chatIssueId ? { issue_id: chatIssueId } : {}),
      });
      if (generation !== chatGenerationRef.current) return;
      setChatHistory(h => [...h, { role: "assistant", content: res.data.response }]);
      loadProviderStatus();
    } catch (err) {
      if (generation !== chatGenerationRef.current) return;
      setChatHistory(h => [...h, { role: "assistant", content: typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : "Sorry, I encountered an error. Please try again." }]);
    } finally {
      if (generation === chatGenerationRef.current) setChatLoading(false);
    }
  };

  // ─── Score Section ───────────────────────────────────────────────────────
  const score = scanResults?.score;
  const isMockScore = score?.data_mode === "mock";

  // ─── Stage labels (map real backend values) ─────────────────────────────
  const getStageLabel = (stage) => {
    const m = { crawling: "Analyzing website…", ai_analysis: "Processing signals…", deep_audit_pending: "Preparing insights…", queued: "Preparing analysis…" };
    return m[stage] || m.queued;
  };
  const getStageDesc = (stage) => {
    const m = { crawling: "Discovering and scanning priority pages", ai_analysis: "Evaluating structure and content quality", deep_audit_pending: "Generating initial recommendations" };
    return m[stage] || null;
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="adviser-dashboard min-h-screen bg-[var(--adv-bg)] transition-colors duration-300" ref={mainAreaRef}>
      <PageSEO title="Search Adviser Dashboard | AI Search Advisers" isPrivate={true} />
      {/* Cursor Glow */}
      <div ref={cursorGlowRef} className="adv-cursor-glow" style={{ opacity: 0 }} />

      {/* ── Header ── */}
      <header className={`adv-header sticky top-0 z-50 transition-all duration-300 ${headerScrolled ? "adv-header--scrolled" : ""}`}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 bg-gradient-to-br from-[#FEAB3D] to-[#e8960a] rounded-lg flex items-center justify-center shadow-sm">
                <Sparkles className="w-4 h-4 text-[#021426]" />
              </div>
              <span className="text-lg font-black tracking-tight text-[var(--adv-text-primary)]">
                AI Search <span className="text-[#FEAB3D]">Adviser</span>
              </span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-[var(--adv-border)]" />
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}
              className="hidden sm:flex items-center gap-1.5 text-[var(--adv-text-secondary)] hover:text-[var(--adv-text-primary)] text-xs">
              <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-3">
            {providerStatus?.ai_provider?.status === "connected" && (
              <div className="hidden md:flex items-center gap-2 text-xs text-[var(--adv-text-secondary)]">
                <span className="adv-status-dot" />
                <span className="font-medium">AI Adviser Online</span>
              </div>
            )}
            <ThemeToggle />
            <div className="hidden sm:block w-px h-6 bg-[var(--adv-border)]" />
            <span className="hidden sm:block text-sm text-[var(--adv-text-secondary)] font-medium">{(user?.first_name?.trim() || user?.name?.trim() || 'User').split(/\s+/)[0]}</span>
            {onLogout && (
              <Button variant="ghost" size="sm" onClick={onLogout} className="text-[var(--adv-text-secondary)] hover:text-red-500 h-8 w-8 p-0" title="Sign Out">
                <LogOut className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* ── Main Layout ── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8 grid grid-cols-12 gap-5 lg:gap-6">

        {/* ── Sidebar ── */}
        <aside className="col-span-12 md:col-span-3 space-y-4">
          <div className="adv-card p-5 space-y-3">
            <p className="text-xs font-semibold text-[var(--adv-text-secondary)] uppercase tracking-wider">Audit a Website</p>
            <div className="adv-input-glow rounded-xl">
              <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com"
                onKeyDown={e => e.key === "Enter" && startScan()} className="text-sm bg-transparent border-[var(--adv-border)] focus-visible:ring-[#FEAB3D]/30 rounded-xl h-10" />
            </div>
            <button className="adv-btn-gold w-full flex items-center justify-center gap-2 h-10 text-sm" onClick={startScan} disabled={startingLoad || !url.trim()}>
              {startingLoad ? <><Loader2 className="h-4 w-4 animate-spin" /> Starting…</> : <><Search className="h-4 w-4" /> Start AI Scan</>}
            </button>
          </div>

          <div className="adv-card p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-[var(--adv-text-secondary)] uppercase tracking-wider">Scan History</p>
              <button onClick={loadScans} disabled={isRefreshing} className={`text-[var(--adv-text-secondary)] hover:text-[var(--adv-text-primary)] transition-colors p-1 rounded-md hover:bg-[var(--adv-surface-raised)] ${isRefreshing ? "opacity-50 cursor-not-allowed" : ""}`}>
                <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
              </button>
            </div>
            <ScrollArea className="h-64">
              {scans.length === 0 && (
                <p className="text-xs text-[var(--adv-text-secondary)] text-center py-8">No scans yet. Start your first audit above.</p>
              )}
              {scans.map(scan => (
                <button key={scan.id} onClick={() => selectScan(scan)}
                  className={`adv-scan-item w-full text-left p-3 rounded-xl mb-1.5 transition-all duration-150 ${
                    selectedScan?.id === scan.id ? "adv-scan-item--selected" : "hover:bg-[var(--adv-surface-raised)]"
                  }`}>
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Globe className="h-3.5 w-3.5 flex-shrink-0 text-[var(--adv-text-secondary)]" />
                      <span className="text-xs font-medium truncate text-[var(--adv-text-primary)]">{scan.url?.replace(/^https?:\/\//, "") || "—"}</span>
                    </div>
                    <div className="flex-shrink-0">
                      <StatusPill status={scan.status} />
                    </div>
                  </div>
                  <p className="text-[11px] text-[var(--adv-text-secondary)] mt-1 pl-5">{scan.created_at ? new Date(scan.created_at).toLocaleDateString() : ""}</p>
                </button>
              ))}
            </ScrollArea>
          </div>

          {providerStatus && (
            <div className="adv-card p-4 space-y-2.5">
              <p className="text-[11px] font-semibold text-[var(--adv-text-secondary)] uppercase tracking-wider">Integrations</p>
              {[
                { label: "AI Provider", val: providerStatus.ai_provider?.status === "connected" ? "Connected" : "Not Connected", live: providerStatus.ai_provider?.status === "connected" },
                { label: "Google Search Console", val: providerStatus.google_search_console?.status === "connected" ? "Connected" : "Not Connected", live: false },
                { label: "SERP Visibility", val: "Not Measured", live: false },
                { label: "ChatGPT Visibility", val: "Not Measured", live: false },
                { label: "Claude Visibility", val: "Not Measured", live: false },
                { label: "Gemini Visibility", val: "Not Measured", live: false },
                { label: "Perplexity Visibility", val: "Not Measured", live: false },
              ].map(({ label, val, live }) => (
                <div key={label} className="flex items-center justify-between text-xs">
                  <span className="text-[var(--adv-text-secondary)]">{label}</span>
                  <span className={`font-semibold ${live ? "text-emerald-500" : "text-[var(--adv-text-secondary)] opacity-60"}`}>{val}</span>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* ── Main Content ── */}
        <main className="col-span-12 md:col-span-9 space-y-5 min-w-0">

          {/* ─── EMPTY STATE ─── */}
          {!selectedScan && (
            <div className="adv-card adv-card--glow flex flex-col items-center justify-center py-16 sm:py-20 text-center relative overflow-hidden">
              <div className="adv-grid-bg absolute inset-0" />
              <div className="relative z-10 flex flex-col items-center">
                <AICore />
                <h2 className="text-2xl sm:text-3xl font-bold text-[var(--adv-text-primary)] mt-8 mb-2 tracking-tight">AI Search Advisers</h2>
                <p className="text-[var(--adv-text-secondary)] text-sm sm:text-base max-w-md mb-8">Select a completed website scan from Scan History to ask questions about its SEO and AI-search findings.</p>
              </div>
            </div>
          )}

          {/* ─── SCAN PROGRESS ─── */}
          {selectedScan && (scanStatus === "queued" || (scanStatus === "running" && !selectedScan.initial_report_ready)) && (
            <div className="adv-card adv-card--glow p-8 sm:p-12 flex flex-col items-center justify-center text-center relative overflow-hidden">
              <div className="adv-grid-bg absolute inset-0 opacity-50" />
              <div className="relative z-10 flex flex-col items-center w-full max-w-md">
                <AICore scale={0.65} />
                <p className="font-semibold text-lg text-[var(--adv-text-primary)] mt-4 mb-3">{getStageLabel(selectedScan.progress_stage)}</p>
                <div className="w-full bg-[var(--adv-surface-raised)] rounded-full h-1.5 mb-3">
                  <div className="bg-[#FEAB3D] h-1.5 rounded-full transition-all duration-700 ease-out" style={{ width: `${selectedScan.progress_percent || 10}%` }} />
                </div>
                {getStageDesc(selectedScan.progress_stage) && (
                  <p className="text-sm text-[var(--adv-text-secondary)]">{getStageDesc(selectedScan.progress_stage)}</p>
                )}
                <p className="text-[var(--adv-text-secondary)] text-xs mt-6 opacity-60">Preparing the report from crawled pages and AI analysis.</p>
              </div>
            </div>
          )}

          {/* ─── SCAN FAILED ─── */}
          {selectedScan && scanStatus === "failed" && (
            <div className="adv-card p-6 border-red-500/20">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="font-semibold text-[var(--adv-text-primary)] mb-1">Something interrupted the analysis</p>
                  <p className="text-sm text-[var(--adv-text-secondary)]">{selectedScan.error_message || "An unknown error occurred. Please try again."}</p>
                </div>
              </div>
            </div>
          )}

          {/* ─── RESULTS ─── */}
          {selectedScan && (["completed", "partial"].includes(scanStatus) || selectedScan.initial_report_ready) && (
            <>
              {/* Deep Audit Background Progress */}
              {scanStatus === "running" && selectedScan.initial_report_ready && (
                <div className="adv-card p-3 flex items-center justify-between" style={{ borderColor: 'rgba(254,171,61,0.2)', background: 'rgba(254,171,61,0.04)' }}>
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-4 w-4 text-[#FEAB3D] animate-spin" />
                    <div>
                      <p className="text-sm font-semibold text-[#FEAB3D]">Deep analysis continuing in background</p>
                      <p className="text-xs text-[#FEAB3D]/70 capitalize">{selectedScan.progress_stage?.replace(/_/g, " ") || "Processing"}</p>
                    </div>
                  </div>
                  <div className="w-24 hidden sm:block rounded-full h-1" style={{ background: 'rgba(254,171,61,0.2)' }}>
                    <div className="bg-[#FEAB3D] h-1 rounded-full transition-all duration-500" style={{ width: `${selectedScan.progress_percent || 30}%` }} />
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="flex flex-wrap gap-0.5 border-b border-[var(--adv-border)] -mx-1 px-1">
                {[
                  { id: "overview", label: "Overview", icon: BarChart3 },
                  { id: "page_audit", label: "Page Audit", icon: Globe },
                  { id: "issues", label: "Issues & Fixes", icon: AlertTriangle },
                  { id: "content_opportunities", label: "Opportunities", icon: Zap },
                  { id: "action_plan", label: "Action Plan", icon: CheckCircle2 },
                  { id: "adviser", label: "AI Adviser", icon: MessageSquare },
                ].map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => setTab(id)}
                    className={`adv-tab flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-all duration-200 ${
                      tab === id ? "adv-tab--active" : "border-transparent text-[var(--adv-text-secondary)] hover:text-[var(--adv-text-primary)]"
                    }`}>
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>

              {/* ── OVERVIEW TAB ── */}
              {tab === "overview" && (
                <div className="space-y-5">
                  {/* Score Card */}
                  <div className="adv-card adv-card--glow p-6 adv-fade-in">
                    <div className="flex items-start justify-between flex-wrap gap-6">
                      <div>
                        <p className="text-sm text-[var(--adv-text-secondary)] font-medium mb-1">AI Search Readiness Score</p>
                        {scanStatus === "running" && (
                          <Badge className="bg-[#FEAB3D]/15 text-[#FEAB3D] border-[#FEAB3D]/30 text-[11px] mb-2">Initial Score</Badge>
                        )}
                        <div className="flex items-end gap-3">
                          <span className={`text-6xl font-extrabold tracking-tight ${
                            (score?.overall_score || 0) >= 70 ? "text-emerald-500" :
                            (score?.overall_score || 0) >= 40 ? "text-amber-500" : "text-red-500"
                          }`}>
                            {resultsLoading ? "—" : (score?.overall_score ?? selectedScan.final_readiness_score ?? selectedScan.initial_readiness_score ?? "—")}
                          </span>
                          <span className="text-2xl text-[var(--adv-text-secondary)] mb-1.5 opacity-50">/ 100</span>
                        </div>
                        <p className="text-xs text-[var(--adv-text-secondary)] mt-1.5">
                          {scanStatus === "running" ? "Score refined after deep audit completes" : scanStatus === "partial" ? "Partial Analysis" : "Completed Analysis"}
                        </p>
                      </div>
                      {score?.component_scores && (
                        <div className="space-y-3 w-full sm:w-auto sm:min-w-56">
                          {Object.entries(score.component_scores).map(([key, val]) => (
                            <div key={key}>
                              <div className="flex justify-between text-xs mb-1.5">
                                <span className="text-[var(--adv-text-secondary)] capitalize">{key.replace(/_/g, " ")}</span>
                                <span className="font-semibold text-[var(--adv-text-primary)]">{val}</span>
                              </div>
                              <div className="adv-score-bar">
                                <div className="adv-score-bar__fill" style={{ "--fill": `${val}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {isMockScore && (
                      <div className="mt-4 p-3 rounded-xl text-xs text-[#FEAB3D]" style={{ background: 'rgba(254,171,61,0.06)', border: '1px solid rgba(254,171,61,0.15)' }}>
                        <strong>Note:</strong> This is an estimated score from demonstration data. Connect live Google Search Console and SERP providers for verified results.
                      </div>
                    )}
                  </div>

                  {/* Analysis Quality */}
                  <div className="adv-card p-5 space-y-3 adv-fade-in adv-stagger-1">
                    <div className="flex items-center gap-3"><h3 className="font-semibold text-[var(--adv-text-primary)]">Analysis Quality</h3><StatusPill status={scanStatus} /></div>
                    <p className="text-sm text-[var(--adv-text-secondary)]">Readiness is based on analyzed website content. External platform visibility is not currently measured.</p>
                    <div className="flex flex-wrap gap-2">{Object.entries(scanResults?.section_statuses || {}).map(([name, section]) => <Badge key={name} variant="secondary" className="capitalize text-[11px] bg-[var(--adv-surface-raised)] text-[var(--adv-text-secondary)] border-[var(--adv-border)]">{name.replace(/_/g, " ")}: {section.status === "fallback" ? "Available from findings" : section.status}{section.generation_mode === "deterministic" ? " · Prioritized" : ""}</Badge>)}</div>
                  </div>

                  {/* Top Priorities */}
                  <div className="adv-card p-6 adv-fade-in adv-stagger-2" style={{ borderColor: 'rgba(254,171,61,0.12)' }}>
                    <div className="flex items-center gap-2 mb-5">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(254,171,61,0.1)' }}>
                        <Zap className="h-4 w-4 text-[#FEAB3D]" />
                      </div>
                      <h3 className="font-bold text-lg text-[var(--adv-text-primary)]">Top Priorities</h3>
                    </div>
                    {scanStatus === "running" && (!scanResults?.issues || scanResults.issues.length === 0) ? (
                      <div className="flex flex-col items-center justify-center py-8">
                        <div className="adv-thinking mb-3">
                          {[0, 1, 2].map(i => (<span key={i} className="adv-thinking__dot w-2 h-2 rounded-full bg-[#FEAB3D]" style={{ animationDelay: `${i * 0.2}s` }} />))}
                        </div>
                        <p className="text-sm text-[var(--adv-text-secondary)]">Generating your top priorities…</p>
                      </div>
                    ) : scanResults?.top_priorities && scanResults.top_priorities.length > 0 ? (
                      <div className="space-y-3">
                        {scanResults.top_priorities.slice(0, 5).map((issue, idx) => {
                          const rec = (scanResults?.recommendations || []).find(r => r.issue_id === issue.id);
                          return (
                            <div key={issue.id} className="adv-card p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 adv-fade-in" style={{ animationDelay: `${idx * 70}ms` }}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                  <span className="font-semibold text-[var(--adv-text-primary)] text-sm">{issue.title}</span>
                                  {rec?.impact === "high" && rec?.effort === "low" && (
                                    <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 border-emerald-500/20 text-[10px] flex-shrink-0">Quick Win</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-[var(--adv-text-secondary)] mb-2">{issue.explanation}</p>
                                <div className="flex items-center gap-2 text-xs text-[var(--adv-text-secondary)] opacity-60">
                                  <Globe className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate w-full" title={issue.affected_url}>{issue.affected_url || "Site-wide"}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 text-xs">
                                {rec && (
                                  <>
                                    <div className="flex flex-col items-end">
                                      <span className="text-[var(--adv-text-secondary)] opacity-60">Impact</span>
                                      <span className="font-semibold text-[var(--adv-text-primary)] capitalize">{rec.impact}</span>
                                    </div>
                                    <div className="flex flex-col items-end">
                                      <span className="text-[var(--adv-text-secondary)] opacity-60">Effort</span>
                                      <span className="font-semibold text-[var(--adv-text-primary)] capitalize">{rec.effort}</span>
                                    </div>
                                  </>
                                )}
                                <Button variant="outline" size="sm" onClick={() => { setSelectedIssueId(issue.id); setTab("issues"); }}
                                  className="text-[#FEAB3D] border-[#FEAB3D]/30 hover:bg-[#FEAB3D]/10 text-xs">View Fix</Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-[var(--adv-text-secondary)] text-center py-4">No top priorities found.</p>
                    )}
                  </div>

                  {/* Comparison */}
                  {scanResults?.comparison && scanResults.comparison.previous_scan_id && (
                    <div className="adv-card p-6 adv-fade-in adv-stagger-3" style={{ background: 'rgba(254,171,61,0.04)', borderColor: 'rgba(254,171,61,0.12)' }}>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(254,171,61,0.1)' }}>
                          <Activity className="h-4 w-4 text-[#FEAB3D]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#FEAB3D] mb-1">Scan Comparison</h3>
                          <p className="text-sm text-[var(--adv-text-secondary)] leading-relaxed">{scanResults.comparison.ai_explanation}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Business Analysis */}
                  {scanResults?.analysis && (
                    <div className="adv-card p-6 space-y-4 adv-fade-in adv-stagger-4">
                      <h3 className="font-semibold text-base text-[var(--adv-text-primary)]">Site Understanding</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="col-span-1 md:col-span-2">
                          <p className="text-[var(--adv-text-secondary)] font-medium mb-1">Business Summary</p>
                          <p className="text-[var(--adv-text-primary)]">{scanResults.analysis.business_summary || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[var(--adv-text-secondary)] font-medium mb-1">Business Type</p>
                          <p className="text-[var(--adv-text-primary)]">{scanResults.analysis.business_type || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[var(--adv-text-secondary)] font-medium mb-1">Target Audience</p>
                          <p className="text-[var(--adv-text-primary)]">{scanResults.analysis.target_audience || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[var(--adv-text-secondary)] font-medium mb-1">Primary Services</p>
                          <div className="flex flex-wrap gap-1">
                            {(scanResults.analysis.primary_services || []).map((s, i) => (
                              <Badge key={i} variant="secondary" className="text-xs bg-[var(--adv-surface-raised)] text-[var(--adv-text-secondary)]">{s}</Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-[var(--adv-text-secondary)] font-medium mb-1">Conversion Goals</p>
                          <div className="flex flex-wrap gap-1">
                            {(scanResults.analysis.conversion_goals || []).map((g, i) => (
                              <Badge key={i} variant="outline" className="text-xs border-[var(--adv-border)] text-[var(--adv-text-secondary)]">{g}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Strengths & Weaknesses */}
                  <div className="adv-card p-6 space-y-5">
                    <div>
                      <h3 className="font-semibold text-[var(--adv-text-primary)] mb-3">Main Strengths</h3>
                      <ul className="list-disc pl-5 space-y-2 text-sm text-[var(--adv-text-secondary)]">{(scanResults?.strengths || []).map((v, i) => <li key={i}>{v}</li>)}</ul>
                    </div>
                    <div className="border-t border-[var(--adv-border)] pt-5">
                      <h3 className="font-semibold text-[var(--adv-text-primary)] mb-3">Main Weaknesses</h3>
                      <ul className="list-disc pl-5 space-y-2 text-sm text-[var(--adv-text-secondary)]">{(scanResults?.weaknesses || []).map((v, i) => <li key={i}>{v}</li>)}</ul>
                    </div>
                  </div>

                  {/* Keywords */}
                  {scanResults?.keywords && scanResults.keywords.length > 0 && (
                    <div className="adv-card p-6">
                      <h3 className="font-semibold text-base text-[var(--adv-text-primary)] mb-4">Target Keywords & Questions</h3>
                      <div className="flex flex-wrap gap-2">
                        {scanResults.keywords.slice(0, 20).map((kw, i) => (
                          <span key={i} className="px-3 py-1.5 bg-[var(--adv-surface-raised)] rounded-full text-xs font-medium text-[var(--adv-text-primary)]">
                            {kw.query}<span className="ml-1.5 text-[var(--adv-text-secondary)] opacity-60">· {kw.intent}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── PAGE AUDIT TAB ── */}
              {tab === "page_audit" && (
                <PageAuditTable scanId={selectedScan.id} scanStatus={scanStatus} isInitialReportReady={selectedScan.initial_report_ready} />
              )}

              {/* ── CONTENT OPPORTUNITIES TAB ── */}
              {tab === "content_opportunities" && (
                <ContentOpportunitiesBoard scan={scanResults || selectedScan} resultsLoading={resultsLoading} />
              )}

              {/* ── ACTION PLAN TAB ── */}
              {tab === "action_plan" && (
                <ActionPlanTimeline scan={scanResults || selectedScan} resultsLoading={resultsLoading} />
              )}

              {/* ── ISSUES TAB ── */}
              {tab === "issues" && (
                <IssuesAndFixesList selectedIssueId={selectedIssueId} scan={scanResults || selectedScan} resultsLoading={resultsLoading}
                  onAskAdviser={issue => { setChatIssueId(issue.id); setChatInput("How do I fix this?"); setTab("adviser"); }} />
              )}

              {/* ── ADVISER CHAT TAB ── */}
              {tab === "adviser" && (
                <div className="flex flex-col h-[70vh] min-h-[450px] max-h-[650px]">
                  <div className="adv-card flex flex-col flex-1 overflow-hidden p-0">
                    <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--adv-border)]">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(254,171,61,0.1)' }}>
                        <Bot className="h-4 w-4 text-[#FEAB3D]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-[var(--adv-text-primary)] truncate">Personal Search Adviser</p>
                        <p className="text-xs text-[var(--adv-text-secondary)]">{selectedDomain} · Scan: {selectedScan.created_at ? new Date(selectedScan.created_at).toLocaleDateString() : 'Date unavailable'}</p>
                        <p className="text-xs text-[var(--adv-text-secondary)] truncate">
                          Powered by AI · Answers grounded in your scan data
                          {isMockScore && " · Some data is simulated"}
                        </p>
                      </div>
                      <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                        <span className="adv-status-dot flex-shrink-0" />
                        <span className="text-[11px] text-[var(--adv-text-secondary)] hidden sm:inline">Online</span>
                      </div>
                    </div>

                    {chatIssue && <div className="px-5 py-3 text-sm border-b border-[var(--adv-border)]">Selected issue: {chatIssue.title} <button className="ml-2 underline" onClick={() => setChatIssueId(null)}>Clear</button></div>}
                    {!chatReady && <p className="px-5 py-3 text-sm">Select a completed website scan from Scan History to ask questions about its SEO and AI-search findings.</p>}

                    <ScrollArea className="flex-1 p-5">
                      {chatHistory.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-full text-center py-12">
                          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(254,171,61,0.1)' }}>
                            <MessageSquare className="h-6 w-6 text-[#FEAB3D]" />
                          </div>
                          <p className="text-[var(--adv-text-primary)] text-sm font-semibold mb-1">Ask about your audit</p>
                          <p className="text-[var(--adv-text-secondary)] text-xs mb-6 max-w-sm">
                            Get explanations and step-by-step fixes for SEO and AI-search issues found in the selected website scan.
                          </p>
                          <div className="flex flex-wrap justify-center gap-2">
                            {suggestedPrompts.map(q => (
                              <button key={q} onClick={() => setChatInput(q)}
                                className="px-3.5 py-2 rounded-full text-xs font-medium border border-[var(--adv-border)] text-[var(--adv-text-secondary)] hover:border-[#FEAB3D] hover:text-[#FEAB3D] transition-all duration-200 hover:-translate-y-0.5">
                                {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {chatHistory.map((msg, i) => (
                        <div key={i} className={`mb-4 flex ${msg.role === "user" ? "justify-end" : "justify-start"} ${i === chatHistory.length - 1 ? "adv-chat-msg--new" : ""}`}>
                          {msg.role === "assistant" && (
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-2 mt-1 flex-shrink-0" style={{ background: 'rgba(254,171,61,0.1)' }}>
                              <Bot className="h-3.5 w-3.5 text-[#FEAB3D]" />
                            </div>
                          )}
                          <div className={`max-w-[85%] md:max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed overflow-x-auto ${
                            msg.role === "user"
                              ? "bg-[#FEAB3D] text-[#021426] rounded-br-md font-medium"
                              : "bg-[var(--adv-surface-raised)] text-[var(--adv-text-primary)] rounded-bl-md"
                          }`}>
                            <div className="adviser-markdown min-w-0"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
                          </div>
                        </div>
                      ))}

                      {chatLoading && (
                        <div className="flex justify-start mb-4 adv-chat-msg--new">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-2 mt-1 flex-shrink-0" style={{ background: 'rgba(254,171,61,0.1)' }}>
                            <Bot className="h-3.5 w-3.5 text-[#FEAB3D]" />
                          </div>
                          <div className="bg-[var(--adv-surface-raised)] px-5 py-3.5 rounded-2xl rounded-bl-md">
                            <div className="flex items-center gap-2">
                              <div className="adv-thinking">
                                {[0, 1, 2].map(i => (<span key={i} className="adv-thinking__dot w-2 h-2 rounded-full bg-[#FEAB3D]" style={{ animationDelay: `${i * 0.2}s` }} />))}
                              </div>
                              <span className="text-xs text-[var(--adv-text-secondary)]">Analyzing your request</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </ScrollArea>

                    <div className="px-5 py-4 border-t border-[var(--adv-border)]">
                      <div className="adv-input-glow flex gap-3 rounded-xl">
                        <Input value={chatInput} onChange={e => setChatInput(e.target.value)}
                          placeholder={`Ask about issues or fixes for ${selectedDomain}...`}
                          maxLength={4000}
                          onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendChat()}
                          className="flex-1 text-sm bg-transparent border-[var(--adv-border)] focus-visible:ring-[#FEAB3D]/30 rounded-xl h-10" disabled={chatLoading || historyLoading || !chatReady} />
                        <button onClick={sendChat} disabled={chatLoading || historyLoading || !chatReady || !chatInput.trim()}
                          className="adv-btn-gold h-10 w-10 flex items-center justify-center rounded-xl disabled:opacity-40 disabled:cursor-not-allowed">
                          {chatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
